import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, MoveRight } from 'lucide-react';
import type { Artwork } from '@/types';
import { useTranslation } from '@/hooks/useTranslation';

interface SuperSliderProps {
  artworks: Artwork[];
}

const SuperSlider: React.FC<SuperSliderProps> = ({ artworks }) => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const navigate = useNavigate();
  const { localize } = useTranslation();

  if (artworks.length === 0) return null;

  const next = (): void => {
    setDirection(1);
    setIndex((prev) => (prev + 1) % artworks.length);
  };
  const prev = (): void => {
    setDirection(-1);
    setIndex((prev) => (prev - 1 + artworks.length) % artworks.length);
  };

  const currentArt = artworks[index]!;

  return (
    <section className="relative h-[70vh] md:h-[85vh] bg-[#fcfcfc] dark:bg-[#050505] flex flex-col md:flex-row overflow-hidden border-y border-black/5 dark:border-white/5 transition-colors duration-700">
      {/* Left Text Column */}
      <div className="w-full md:w-[40%] h-1/2 md:h-full p-6 sm:p-10 md:p-24 flex flex-col justify-center order-2 md:order-1 bg-white dark:bg-black relative z-10 transition-colors duration-700">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentArt._id}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className="space-y-8"
          >
            <span className="text-[10px] tracking-[0.4em] font-bold text-black/20 dark:text-white/20 uppercase">
              Featured // 0{index + 1}
            </span>
            <h2 className="text-4xl md:text-7xl font-syncopate font-bold text-black dark:text-white leading-none tracking-tighter uppercase transition-colors duration-700">
              {localize(currentArt.title)}
            </h2>
            <div className="flex items-center gap-4 pt-4 flex-wrap">
              <button
                onClick={() => navigate(`/artwork/${currentArt.slug}`)}
                className="group flex items-center gap-4 text-black dark:text-white transition-colors duration-700"
              >
                <div className="w-14 h-14 rounded-full border border-black/5 dark:border-white/5 flex items-center justify-center group-hover:bg-black group-hover:text-white dark:group-hover:bg-white dark:group-hover:text-black transition-all shadow-sm">
                  <MoveRight size={20} />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest">
                  View Details
                </span>
              </button>

              <div className="flex gap-3 md:hidden ml-auto">
                <button
                  onClick={prev}
                  className="w-10 h-10 rounded-full border border-black/5 dark:border-white/5 flex items-center justify-center text-black dark:text-white hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={next}
                  className="w-10 h-10 rounded-full border border-black/5 dark:border-white/5 flex items-center justify-center text-black dark:text-white hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="absolute bottom-10 left-10 md:left-24 hidden md:flex gap-4">
          <button
            onClick={prev}
            className="w-12 h-12 rounded-full border border-black/5 dark:border-white/5 flex items-center justify-center text-black dark:text-white hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={next}
            className="w-12 h-12 rounded-full border border-black/5 dark:border-white/5 flex items-center justify-center text-black dark:text-white hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Right Visual Column */}
      <div className="w-full md:w-[60%] h-1/2 md:h-full order-1 md:order-2 relative bg-white dark:bg-black transition-colors duration-700 p-0 md:p-10 md:pr-20 lg:p-16 lg:pr-24 flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentArt._id}
            initial={{ opacity: 0, filter: 'blur(20px)' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, filter: 'blur(20px)' }}
            transition={{ duration: 1 }}
            className="w-full h-full md:rounded-2xl overflow-hidden shadow-2xl shadow-black/10 dark:shadow-white/5 relative"
          >
            <img
              src={currentArt.images?.[0] || ''}
              className="w-full h-full object-cover grayscale-[0.3] hover:grayscale-0 transition-all duration-1000"
              alt={localize(currentArt.title)}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white/10 dark:from-black/10 to-transparent pointer-events-none transition-colors duration-700" />
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default SuperSlider;
